import { useState } from 'react';
import { DbuiV1GetObservableTop10Response } from '@antimatterhq/asl-api-ts-v1';
import { Icon } from '@antimatterhq/ui/components/icon';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Badge } from '@antimatterhq/ui/components/badge';
import { Button } from '@antimatterhq/ui/components/button';
import * as format from '@antimatterhq/ui/utils/format';
import { Text } from '@antimatterhq/ui/components/typography';
import {
  SimpleTable,
  SimpleTableCell,
  SimpleTableBody,
  SimpleTableSkeletonRows,
  SimpleTableRow,
  SimpleTableHeader,
  SimpleTableHead,
} from '@antimatterhq/ui/components/simpleTable';
import Table from '@antimatterhq/ui/components/table';
import { EmptyTable } from '@/components/table/empty/EmptyTable';
export type ObservableItem = NonNullable<
  NonNullable<DbuiV1GetObservableTop10Response['items']>[number]['top10']
>[number];

export type BaseObservablesTableProps = {
  name: string;
  items?: ObservableItem[];
  maxRowCount?: number;
  showActions?: boolean;
  onEdit?: (item: ObservableItem) => void;
  onView?: (item: ObservableItem) => void;
};

const ObservablesTableValueCell = ({ item }: { item: ObservableItem }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    if (!item.value) return;
    await navigator.clipboard.writeText(item.value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>
        <SimpleTableCell className="w-auto truncate">
          {item.value}
        </SimpleTableCell>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content align="center">
          <div className="dark:bg-grey-900 flex items-center rounded bg-grey-700 pb-1 pl-3 pr-1 pt-1">
            <Text className="text-white">{item.value}</Text>
            <Button
              className="w-2"
              size="small"
              variant="ghost"
              onClick={copyToClipboard}
            >
              <Icon
                icon={copied ? 'lucide:check' : 'lucide:copy'}
                className="text-white"
              />
            </Button>
          </div>
          <TooltipPrimitive.Arrow className="dark:fill-grey-900 fill-grey-700" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};

export const BaseObservablesTable = ({
  name,
  items,
  maxRowCount,
  showActions = false,
  onEdit,
  onView,
}: BaseObservablesTableProps) => {
  const filteredItems = items
    ? maxRowCount !== undefined
      ? items.slice(0, maxRowCount)
      : items
    : undefined;

  return (
    <SimpleTable className="w-full" fixed>
      <SimpleTableHeader>
        <SimpleTableRow>
          <SimpleTableHead className="w-auto truncate font-bold">
            {name}
          </SimpleTableHead>
          <SimpleTableHead className="w-[100px] font-bold">
            Risk Score
          </SimpleTableHead>
          {showActions && <SimpleTableHead className="w-[50px]" />}
        </SimpleTableRow>
      </SimpleTableHeader>
      <SimpleTableBody>
        {!filteredItems && (
          <SimpleTableSkeletonRows
            rows={maxRowCount ?? 10}
            cols={showActions ? 3 : 2}
          />
        )}
        {!!filteredItems && !filteredItems.length && (
          <EmptyTable cols={showActions ? 3 : 2} />
        )}
        {!!filteredItems &&
          !!filteredItems.length &&
          filteredItems.map((item) => (
            <SimpleTableRow key={`${name}-${item.value}`} hover>
              <ObservablesTableValueCell item={item} />
              <SimpleTableCell className="w-[100px]">
                <Badge variant="filled" color="info">
                  {format.count(item.riskScore)}
                </Badge>
              </SimpleTableCell>
              {showActions && (
                <SimpleTableCell className="relative w-[50px]">
                  <div className="absolute inset-y-0 right-0 flex items-center gap-1">
                    <Table.ActionsCell
                      data-testid={item.value || ''}
                      actions={[
                        {
                          label: 'Modify risk score',
                          onClick: () => onEdit?.(item),
                          icon: 'antimatterhq:pencil',
                        },
                        {
                          label: 'See events',
                          onClick: () => onView?.(item),
                          icon: 'lucide:list',
                        },
                      ]}
                    />
                  </div>
                </SimpleTableCell>
              )}
            </SimpleTableRow>
          ))}
      </SimpleTableBody>
    </SimpleTable>
  );
};
